import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  fetchImages,
  uploadImageWithProgress,
  deleteImage,
} from "../thunksapi/uploadImageThunk";
import { UploadedImage } from "../types/index";

interface ModalState {
  isOpen: boolean;
  selectedImages: UploadedImage[];
  loading: boolean;
  error: string | null;
}

const initialState: ModalState = {
  isOpen: false,
  selectedImages: [],
  loading: false,
  error: null,
};

const modalSlice = createSlice({
  name: "modal",
  initialState,
  reducers: {
    openModal: (state) => {
      state.isOpen = true;
    },
    closeModal: (state) => {
      state.isOpen = false;
    },
    removeError: (state) => {
      state.error = "";
    },
    selectImage: (state, action: PayloadAction<UploadedImage>) => {
      state.selectedImages.push(action.payload);
    },
    removeImage: (state, action: PayloadAction<string>) => {
      state.selectedImages = state.selectedImages.filter(
        (img) => img.id !== action.payload
      );
    },
    updateImageProgress: (
      state,
      action: PayloadAction<{ id: string; progress: number }>
    ) => {
      const image = state.selectedImages.find(
        (img) => img.id === action.payload.id
      );
      if (image) {
        image.progress = action.payload.progress;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchImages.pending, (state) => {
        state.loading = true;
      })
      .addCase(
        fetchImages.fulfilled,
        (state: any, action: PayloadAction<any[]>) => {
          state.loading = false;
          state.selectedImages = action.payload.map((image) => ({
            id: image.image_id,
            name: image.url.split("/").pop() || "Unnamed",
            url: image.url,
            preview: "",
            progress: 100,
            status: "uploaded",
          }));
        }
      )
      .addCase(fetchImages.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to load images";
      })
      .addCase(uploadImageWithProgress.fulfilled, (state, action) => {
        const image = state.selectedImages.find(
          (img) => img.id === action.payload.id
        );
        if (image) {
          image.status = "uploaded";
          image.progress = 100;
          image.url = action.payload.url;
        }
      })
      .addCase(uploadImageWithProgress.rejected, (state, action: any) => {
        const image = state.selectedImages.find(
          (img) => img.id === action.meta.arg.id.toString()
        );
        if (image) {
          image.status = "failed";
        }
        state.error = action.payload?.error || "Upload failed";
      })
      .addCase(deleteImage.fulfilled, (state, action) => {
        state.selectedImages = state.selectedImages.filter(
          (img) => img.id !== action.payload.toString()
        );
      });
  },
});

export const {
  openModal,
  closeModal,
  selectImage,
  removeImage,
  updateImageProgress,
  removeError,
} = modalSlice.actions;

export default modalSlice.reducer;
